import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b';
import type { StytchAPIError } from '@stytch/vanilla-js';
import React, { useEffect, useRef } from 'react';
import { isWindowDefined } from 'smoothie';

import {
    SESSION_DURATION_MINUTES,
    SESSION_RENEWAL_INTERVAL,
} from '../../auth/authConstants';
import type { ContainerComponent } from '../../common/types/Common';
import { captureException, captureMessage } from '../../logger/Logger';

const isUnauthenticatedStytchError = (error: unknown): boolean => {
    if (error == null || typeof error !== 'object') {
        return false;
    }

    const stytchError = error as Partial<StytchAPIError>;
    return (
        stytchError.status_code === 401 ||
        stytchError.error_type === 'user_unauthenticated'
    );
};

const SessionManager: ContainerComponent = ({ children }) => {
    const stytchClient = useStytchB2BClient();
    const { session } = useStytchMemberSession();

    // We need to keep a ref of the session because setInterval cannot detect state changes
    const stytchSessionRef = useRef(session);

    useEffect(() => {
        const renewSession = async () => {
            if (!stytchSessionRef.current) {
                return;
            }

            // Background tabs throttle timers; skip renewals until the tab is visible again.
            if (isWindowDefined && document.hidden) {
                return;
            }

            try {
                await stytchClient.session.authenticate({
                    session_duration_minutes: SESSION_DURATION_MINUTES,
                });
            } catch (error) {
                if (isUnauthenticatedStytchError(error)) {
                    // Expected when the server session expired while local state still looks signed-in.
                    captureMessage(
                        'Stytch session expired during renewal; skipping authenticate',
                    );
                    return;
                }

                captureException(error);
            }
        };

        // Sets up a timer to renew an active session at a given interval
        // Note: <InactivityDetector> will still revoke an active session if it doesn't detect activity within a certain time threshold.
        const interval = setInterval(() => {
            void renewSession();
        }, SESSION_RENEWAL_INTERVAL);

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                void renewSession();
            }
        };

        if (isWindowDefined) {
            document.addEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        }

        return () => {
            clearInterval(interval);
            if (isWindowDefined) {
                document.removeEventListener(
                    'visibilitychange',
                    handleVisibilityChange,
                );
            }
        };
    }, [stytchClient]);

    // Keep the session ref in sync with state.
    useEffect(() => {
        stytchSessionRef.current = session;
    }, [session]);

    return <>{children}</>;
};

export default SessionManager;
